export class ProductInfo extends HTMLElement {
  abortController = undefined;
  swiper = undefined;

  constructor() {
    super();
    this.handleVariantSelectorChange = this.handleVariantSelectorChange.bind(this);
  }

  setupEventListeners() {
    this.addEventListener('change', this.handleVariantSelectorChange);
    this.setupLaunchTeeConfig();

    const quantitySelector = this.quantitySelector;
    if (!quantitySelector) return;

    quantitySelector.addEventListener('change', this.onQuantitySelectorEvent.bind(this));
    quantitySelector.querySelector('button[name="plus"]')?.addEventListener('click', this.onQuantitySelectorEvent.bind(this));
    quantitySelector.querySelector('button[name="minus"]')?.addEventListener('click', this.onQuantitySelectorEvent.bind(this));
  }

  connectedCallback() {
    this.setupEventListeners();
    this.initSwiper();
  }

  initSwiper() {
    const thumbnailGalleryEl = this.querySelector('.product-media-gallery__thumbnails');
    const thumbnailWrapperEl = this.querySelector('.product-media-gallery__thumbnails-wrapper');
    if (thumbnailGalleryEl) {

      const mainGalleryEl = this.querySelector('.product-media-gallery__main');
      if (mainGalleryEl) {
        const prevBtn = thumbnailWrapperEl?.querySelector('.swiper-button-prev');
        const nextBtn = thumbnailWrapperEl?.querySelector('.swiper-button-next');

        const thumbsConfig = {
          spaceBetween: 16,
          slidesPerView: 'auto',
          freeMode: true,
          watchSlidesProgress: true,
          slideToClickedSlide: true,
          breakpoints: {
            768: {
              spaceBetween: 14,
            },
            1024: {
              spaceBetween: 16,
            },
          },
        };

        if (prevBtn && nextBtn) {
          thumbsConfig.navigation = {
            prevEl: prevBtn,
            nextEl: nextBtn,
          };
        }

        const thumbsSwiper = new Swiper(thumbnailGalleryEl, thumbsConfig);

        this.mainSwiper = new Swiper(mainGalleryEl, {
          spaceBetween: 0,
          thumbs: {
            swiper: thumbsSwiper,
          },
        });

        this.swiper = this.mainSwiper;
      }
    }

    const carouselGalleryEl = this.querySelector('.product-media-gallery__carousel');
    if (carouselGalleryEl) {
      this.carouselSwiper = new Swiper(carouselGalleryEl, {
        autoHeight: true,
        direction: 'horizontal',
        pagination: {
          el: carouselGalleryEl.querySelector('.swiper-pagination'),
        },
        navigation: {
          prevEl: carouselGalleryEl.querySelector('.swiper-button-prev'),
          nextEl: carouselGalleryEl.querySelector('.swiper-button-next'),
        },
      });

      if (!this.mainSwiper) {
        this.swiper = this.carouselSwiper;
      }
    }
  }

  get variantSelector() {
    return this.querySelector('variant-selector');
  }

  handleVariantSelectorChange(event) {
    if (!event.target.closest('variant-selector')) return;
    if (event.target.matches('input[type="radio"]') && !event.target.checked) return;
    this.onVariantChange(event);
  }

  getProductData() {
    const productElement = this.querySelector('[data-product]');
    if (!productElement) return null;

    try {
      return JSON.parse(productElement.textContent);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  getSelectedOptionValues() {
    const variantSelector = this.variantSelector;
    if (!variantSelector) return [];

    if (variantSelector.dataset.pickerType === 'dropdown') {
      return Array.from(variantSelector.querySelectorAll('select')).map((select) => select.value);
    }

    return Array.from(variantSelector.querySelectorAll('fieldset input:checked')).map((input) => input.value);
  }

  getVariantFromCurrentSelection() {
    const product = this.getProductData();
    const selectedOptions = this.getSelectedOptionValues();
    if (!product?.variants?.length || !selectedOptions.length) return null;

    return (
      product.variants.find((variant) =>
        selectedOptions.every((value, index) => variant.options[index] === value)
      ) || null
    );
  }

  syncVariantFromSelection() {
    const variant = this.getVariantFromCurrentSelection();
    if (!variant) return;

    this.updateVariantInputs(variant.id);
    this.updateURL(variant.id);

    const variantData = this.variantSelector?.querySelector('[data-selected-variant]');
    if (variantData) {
      variantData.textContent = JSON.stringify(variant);
    }
  }

  get quantitySelector() {
    return this.querySelector('quantity-selector');
  }

  setupLaunchTeeConfig() {
    this.querySelectorAll('[data-launch-tee-config]').forEach((config) => {
      const secondToggle = config.querySelector('[data-launch-tee-second-toggle]');
      const secondSize = config.querySelector('[data-launch-tee-second-size]');
      const secondSelect = secondSize?.querySelector('[data-launch-tee-size]');
      const addButton = config.querySelector('[data-launch-tee-add]');

      secondToggle?.addEventListener('change', () => {
        const isEnabled = secondToggle.checked;
        if (secondSize) secondSize.hidden = !isEnabled;
        if (secondSelect) {
          secondSelect.disabled = !isEnabled;
          secondSelect.required = isEnabled;
          if (!isEnabled) secondSelect.value = '';
        }
      });

      addButton?.addEventListener('click', () => this.addLaunchTeeItems(config, addButton));
    });
  }

  async getLaunchTeeCartQuantity(productHandles) {
    const cartItems = window.liquidAjaxCart?.cart?.items;
    let items = cartItems;

    if (!Array.isArray(items)) {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      items = cart.items;
    }

    if (!Array.isArray(items)) return 0;

    const handles = Array.isArray(productHandles) ? productHandles : [productHandles];
    const memberPerkHandles = new Set(handles.filter(Boolean));

    return items.reduce((total, item) => {
      if (!memberPerkHandles.has(item.handle)) return total;
      return total + item.quantity;
    }, 0);
  }

  async addLaunchTeeItems(config, button) {
    const errors = config.querySelector('[data-ajax-cart-errors="form"]');
    const productHandle = config.dataset.productHandle;
    const memberPerkProductHandles = (config.dataset.memberPerkProductHandles || productHandle)
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);
    const maxQuantity = Number(config.dataset.maxQuantity) || 2;
    const activeSizeSelects = Array.from(config.querySelectorAll('[data-launch-tee-size]')).filter(
      (select) => !select.disabled
    );

    const items = activeSizeSelects
      .map((select, index) => ({
        id: Number(select.value),
        quantity: 1,
        properties: {
          _member_perk_slot: `Item ${index + 1}`,
        },
      }))
      .filter((item) => Number.isFinite(item.id) && item.id > 0)
      .slice(0, 2);

    if (items.length !== activeSizeSelects.length) {
      if (errors) errors.textContent = 'Please select a size for each complimentary tee.';
      return;
    }

    if (!items.length) return;

    const launchTeeQuantityInCart = await this.getLaunchTeeCartQuantity(memberPerkProductHandles);
    const remainingClaimQuantity = Math.max(0, maxQuantity - launchTeeQuantityInCart);

    if (items.length > remainingClaimQuantity) {
      if (errors) {
        errors.textContent =
          remainingClaimQuantity > 0
            ? `You can add ${remainingClaimQuantity} more complimentary launch tee.`
            : `You already have the maximum of ${maxQuantity} complimentary tees in your cart.`;
      }
      return;
    }

    if (errors) errors.textContent = '';
    button.setAttribute('processing', '');
    button.disabled = true;

    const onComplete = (requestState) => {
      button.removeAttribute('processing');
      button.disabled = false;

      if (!requestState?.responseData?.ok) {
        const responseBody = requestState?.responseData?.body;
        if (errors) {
          errors.textContent =
            responseBody?.description || responseBody?.message || 'Unable to add your complimentary tee right now.';
        }
        return;
      }

      if (config.dataset.cartType === 'drawer') {
        window.dispatchEvent(new CustomEvent('cart-open'));
      } else {
        window.location.href = '/cart';
      }
    };

    if (window.liquidAjaxCart?.add) {
      window.liquidAjaxCart.add({ items }, { lastCallback: onComplete });
      return;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    })
      .then((response) => response.json().then((body) => ({ ok: response.ok, body })))
      .then(({ ok, body }) => {
        if (!ok) {
          throw new Error(body?.description || body?.message || 'Unable to add your complimentary tee right now.');
        }
        window.location.href = '/cart';
      })
      .catch((error) => {
        button.removeAttribute('processing');
        button.disabled = false;
        if (errors) errors.textContent = error.message;
      });
  }

  get selectedOptionValues() {
    if (this.variantSelector.dataset.pickerType === 'dropdown') {
      const list = Array.from(this.variantSelector.querySelectorAll('select')).map(
        (select) => select.options[select.selectedIndex].dataset.optionValueId
      );
      return list;
    } else {
      const list = Array.from(this.variantSelector.querySelectorAll('fieldset input:checked')).map(
        ({ dataset }) => dataset.optionValueId
      );
      return list;
    }
  }

  getSelectedVariant(html) {
    const selectedVariant = html.querySelector('[data-selected-variant]')?.innerHTML;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  onVariantChange(e) {
    this.syncVariantFromSelection();
    const productUrlChanged = e.target?.dataset?.productUrl ? (e.target?.dataset?.productUrl !== this.dataset.url) : false;
    const productUrl = e.target?.dataset?.productUrl || this.dataset.url;
    this.renderSection(productUrlChanged, productUrl);
  }

  onQuantitySelectorEvent(e) {
    const quantityInput = this.quantitySelector.querySelector('input[type="number"]');
    let currentValue = parseInt(quantityInput.value);
    const minValue = parseInt(quantityInput.getAttribute('min')) || 0;
    const maxValue = parseInt(quantityInput.getAttribute('max')) || Infinity;

    if (e.target.name === 'minus' && currentValue > minValue) {
      quantityInput.value = currentValue - 1;
    } else if (e.target.name === 'plus' && currentValue < maxValue) {
      quantityInput.value = currentValue + 1;
    } else if (e.type === 'change') {
      if (currentValue < minValue) {
        quantityInput.value = minValue;
      } else if (currentValue > maxValue) {
        quantityInput.value = maxValue;
      }
    }
  }

  updateMedia(variantFeaturedMediaId) {
    if (!variantFeaturedMediaId) return;
    const mediaSlide = this.querySelector(`.swiper-slide[data-media-id="${variantFeaturedMediaId}"]`);
    if (!mediaSlide) return;

    const slideIndex = parseInt(mediaSlide.dataset.mediaIndex);
    this.mainSwiper?.slideTo(slideIndex);
    this.carouselSwiper?.slideTo(slideIndex);
  }

  updateURL(variantId) {
    // this.querySelector('share-button')?.updateUrl(
    //   `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
    // );

    // Don't update URL if this is in a modal/quick-add context
    if (this.dataset.updateUrl === 'false') return;

    if (!window.location.pathname.includes('/products/')) return;
    window.history.replaceState({}, '', `${this.dataset.url}${variantId ? `?variant=${variantId}` : ''}`);
  }

  updateSourceFromDestination = (html, id) => {
    const source = html.getElementById(`${id}`);
    const destination = this.querySelector(`#${id}`);
    if (source && destination) {
      destination.innerHTML = source.innerHTML;
    }
  };

  updateVariantInputs(variantId) {
    this.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`).forEach(
      (productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        input.value = variantId ?? '';
      }
    );
  }

  renderSection(productUrlChanged, productUrl) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    // If the section is in a modal, use the original section id without the modal suffix
    const sectionId = this.dataset.updateUrl === 'false' ? this.dataset.section.split('-modal')[0] : this.dataset.section;
    fetch(`${productUrl}?option_values=${this.selectedOptionValues}&section_id=${sectionId}`, {
      signal: this.abortController.signal,
    })
      .then((response) => response.text())
      .then((responseText) => {
        // If the section is in a modal, replace the original section id with the modal section id
        if (this.dataset.updateUrl === 'false') {
          responseText = responseText.replaceAll(this.dataset.section.split('-modal')[0], this.dataset.section);
        }

        // Parse the response text into an HTML document
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const variant = this.getSelectedVariant(html);
        if (productUrlChanged) {
          // If the product url has changed, replace the current section with the new section
          const productInfo = html.querySelector('product-info');
          this.replaceWith(productInfo);
          productInfo.updateURL(variant?.id);
        } else {
          this.updateMedia(variant?.featured_media?.id);
          this.updateURL(variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateSourceFromDestination(html, `add-to-cart-container-${this.dataset.section}`);
          this.updateSourceFromDestination(html, `variant-selector-${this.dataset.section}`);
          this.updateSourceFromDestination(html, `price-${this.dataset.section}`);
          this.updateSourceFromDestination(html, `sku-${this.dataset.section}`);
          this.updateSourceFromDestination(html, `inventory-${this.dataset.section}`);
          this.syncVariantFromSelection();
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo);
}